import React from 'react';
import { ArrowLeft, PenLine, X } from 'lucide-react';

import { LoadingSpinner, InlineLoadingState } from '../../../components/LoadingSpinner';

const SIGNWELL_EMBED_SCRIPT = 'https://static.signwell.com/assets/embedded.js';
const EMBED_CONTAINER_ID = 'signwell-requesting-embed';

type SignwellEmbedInstance = {
  open: () => void;
  close?: () => void;
  getDocument?: () => Promise<unknown>;
};

type SignwellEmbedConstructor = new (options: {
  url: string;
  containerId?: string;
  allowClose?: boolean;
  showSendButton?: boolean;
  allowShareLink?: boolean;
  allowDownload?: boolean;
  allowAddContacts?: boolean;
  allowCC?: boolean;
  showHeader?: boolean;
  events?: Record<string, (event: unknown) => void>;
}) => SignwellEmbedInstance;

declare global {
  interface Window {
    SignWellEmbed?: SignwellEmbedConstructor;
  }
}

let embedScriptPromise: Promise<void> | null = null;

function waitForSignwellEmbedConstructor(timeoutMs = 10000): Promise<void> {
  return new Promise((resolve, reject) => {
    const started = Date.now();
    const tick = () => {
      if (window.SignWellEmbed) {
        resolve();
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        reject(new Error('SignWell konnte nicht geladen werden.'));
        return;
      }
      window.setTimeout(tick, 50);
    };
    tick();
  });
}

function loadSignwellEmbedScript(): Promise<void> {
  if (window.SignWellEmbed) return Promise.resolve();
  if (!embedScriptPromise) {
    embedScriptPromise = (async () => {
      const existing = document.querySelector(`script[src="${SIGNWELL_EMBED_SCRIPT}"]`);
      if (!existing) {
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement('script');
          script.src = SIGNWELL_EMBED_SCRIPT;
          script.async = true;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('SignWell konnte nicht geladen werden.'));
          document.body.appendChild(script);
        });
      }
      await waitForSignwellEmbedConstructor();
    })().catch((error: unknown) => {
      embedScriptPromise = null;
      throw error;
    });
  }
  return embedScriptPromise;
}

type SignwellRequestingEmbedProps = {
  open: boolean;
  embeddedEditUrl: string | null;
  documentName: string;
  signwellDocumentId: string | null;
  continuing: boolean;
  continueError: string | null;
  onClose: () => void;
  onContinue: () => void;
};

export function SignwellRequestingEmbed({
  open,
  embeddedEditUrl,
  documentName,
  signwellDocumentId,
  continuing,
  continueError,
  onClose,
  onContinue,
}: SignwellRequestingEmbedProps) {
  const embedRef = React.useRef<SignwellEmbedInstance | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open || !embeddedEditUrl) {
      embedRef.current?.close?.();
      embedRef.current = null;
      setLoading(false);
      setLoadError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    void loadSignwellEmbedScript()
      .then(() => {
        if (cancelled || !window.SignWellEmbed) return;
        embedRef.current?.close?.();
        const embed = new window.SignWellEmbed({
          url: embeddedEditUrl,
          containerId: EMBED_CONTAINER_ID,
          allowClose: false,
          showSendButton: false,
          allowShareLink: false,
          allowDownload: true,
          allowAddContacts: false,
          allowCC: false,
          showHeader: true,
          events: {
            error: () => {
              if (!cancelled) setLoadError('SignWell-Editor konnte nicht geladen werden.');
            },
          },
        });
        embedRef.current = embed;
        embed.open();
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : 'SignWell konnte nicht geladen werden.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      embedRef.current?.close?.();
      embedRef.current = null;
    };
  }, [open, embeddedEditUrl]);

  if (!open || !signwellDocumentId) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex flex-col bg-app-surface"
      role="dialog"
      aria-modal="true"
      aria-label="SignWell Feld-Editor"
    >
      <div className="shrink-0 px-4 py-3 border-b border-app-border flex items-center gap-3">
        <button
          type="button"
          onClick={onClose}
          disabled={continuing}
          className="p-1.5 -ml-1 rounded-lg text-app-text-muted hover:bg-app-hover disabled:opacity-50"
          aria-label="Schließen"
        >
          <ArrowLeft className="w-5 h-5 md:hidden" />
          <X className="w-5 h-5 hidden md:block" />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-semibold text-app-text truncate">Signaturfelder setzen</h2>
          <p className="text-xs text-app-text-subtle truncate">{documentName}</p>
        </div>
        <button
          type="button"
          onClick={onContinue}
          disabled={continuing || loading || Boolean(loadError)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-pipedrive-blue px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {continuing ? <LoadingSpinner /> : <PenLine className="w-4 h-4" />}
          <span className="hidden sm:inline">Zur Unterschrift senden</span>
          <span className="sm:hidden">Senden</span>
        </button>
      </div>

      <p className="shrink-0 px-4 py-1.5 text-xs text-app-text-subtle border-b border-app-border">
        Felder im SignWell-Editor platzieren (Unterschrift, Datum, Ort). Anschließend „Zur Unterschrift senden“ klicken.
      </p>

      {(loadError || continueError) ? (
        <div className="mx-4 mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {continueError || loadError}
        </div>
      ) : null}

      <div className="flex-1 min-h-0 relative bg-app-surface-muted">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <InlineLoadingState label="SignWell wird geladen…" />
          </div>
        ) : null}
        <div id={EMBED_CONTAINER_ID} className="absolute inset-0 min-h-0" />
      </div>
    </div>
  );
}
